import { VACANCY_OVERRIDE, type VacancyOverrideType } from "./constants";

import type {
  OverrideImpactItem,
  VacancyOverride,
} from "@/features/segments/types";

export const isInSegment = (
  override: VacancyOverrideType | undefined,
  matchesRules: boolean,
): boolean => (override ? override === VACANCY_OVERRIDE.INCLUDE : matchesRules);

export const isRedundantOverride = (
  override: VacancyOverrideType,
  matchesRules: boolean,
): boolean =>
  override === VACANCY_OVERRIDE.INCLUDE ? matchesRules : !matchesRules;

export const findOverride = (overrides: VacancyOverride[], vacancyId: number) =>
  overrides.find((override) => override.vacancyId === vacancyId)?.type;

export const removeOverrides = (
  overrides: VacancyOverride[],
  vacancyIds: number[],
): VacancyOverride[] =>
  overrides.filter((override) => !vacancyIds.includes(override.vacancyId));

export const revertOverrides = (
  current: VacancyOverride[],
  before: VacancyOverride[],
  vacancyIds: number[],
): VacancyOverride[] => [
  ...removeOverrides(current, vacancyIds),
  ...before.filter((override) => vacancyIds.includes(override.vacancyId)),
];

export const flipOverride = (
  overrides: VacancyOverride[],
  vacancyId: number,
  matchesRules: boolean,
): VacancyOverride[] => {
  const goesIn = !isInSegment(findOverride(overrides, vacancyId), matchesRules);
  const rest = removeOverrides(overrides, [vacancyId]);
  if (goesIn === matchesRules) return rest;
  return [
    ...rest,
    {
      vacancyId,
      type: goesIn ? VACANCY_OVERRIDE.INCLUDE : VACANCY_OVERRIDE.EXCLUDE,
    },
  ];
};

export const OVERRIDE_STATE = {
  ACTIVE: "active",
  REDUNDANT: "redundant",
  AFFECTED: "affected",
  OFFLINE: "offline",
  UNPUBLISHED: "unpublished",
} as const;

export type OverrideState =
  (typeof OVERRIDE_STATE)[keyof typeof OVERRIDE_STATE];

export type OverrideRow = OverrideImpactItem & { state: OverrideState };

export const toOverrideRows = (
  impact: OverrideImpactItem[],
  overrides: VacancyOverride[],
): OverrideRow[] => {
  const impactByVacancy = new Map(
    impact.map((item) => [item.vacancy.vacancyId, item]),
  );
  return overrides.flatMap(({ vacancyId, type }) => {
    const item = impactByVacancy.get(vacancyId);
    if (!item) return [];
    const current = { ...item, type };
    return [{ ...current, state: classifyOverride(current) }];
  });
};

export const getIncludedDeltaOnRemove = (removed: OverrideRow[]) =>
  removed
    .filter((row) => row.state === OVERRIDE_STATE.ACTIVE)
    .reduce(
      (total, row) => total + (row.type === VACANCY_OVERRIDE.EXCLUDE ? 1 : -1),
      0,
    );

export const getAffectedRows = (
  rows: OverrideRow[],
  kept: ReadonlySet<number>,
) =>
  rows.filter(
    (row) =>
      row.state === OVERRIDE_STATE.AFFECTED && !kept.has(row.vacancy.vacancyId),
  );

export const classifyOverride = ({
  type,
  isLive,
  isUnpublished,
  matchesSavedRules,
  matchesDraftRules,
}: Pick<
  OverrideImpactItem,
  | "type"
  | "isLive"
  | "isUnpublished"
  | "matchesSavedRules"
  | "matchesDraftRules"
>): OverrideState => {
  if (isUnpublished) return OVERRIDE_STATE.UNPUBLISHED;
  if (!isLive) return OVERRIDE_STATE.OFFLINE;
  if (!isRedundantOverride(type, matchesDraftRules))
    return OVERRIDE_STATE.ACTIVE;
  if (
    matchesSavedRules !== null &&
    isRedundantOverride(type, matchesSavedRules)
  )
    return OVERRIDE_STATE.REDUNDANT;
  return OVERRIDE_STATE.AFFECTED;
};
